import { useQuery } from '@apollo/client'
import { format, parseISO } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  LoyaltyCardImage,
  LoyaltyCardName,
} from '@src/components/CustomerLoyaltyCard/CustomerLoyaltyCard.styles'
import { LoyaltyCardInfoText } from '@src/components/CustomerLoyaltyCard/LoyaltyCardInfoText'
import { getSingleLoyaltyCardDocument } from '@src/components/CustomerLoyaltyCard/queries/__generated__/getSingleLoyaltyCard.graphql-interface'
import { Content } from '@src/components/Drawer/Drawer.styles'
import { BurgerSection } from '@src/components/Drawer/DrawerHeader'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { LoyaltyCardDiscountStrategy } from '@src/graphql-types'
import { MainRouteName, useAccountRouter } from '@src/hooks/useAccountRouter'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { convertToSlug } from '@src/utils/convertToSlug'
import { imageJitURL } from '@src/utils/imageJitURL'

import {
  LoyaltyCardContainer,
  LoyaltyCardHowManyStampsAwayText,
  LoyaltyInfoContainer,
  ProgressMeterContainer,
  SectionContainer,
  SectionContent,
  SectionTitle,
  StampedAmount,
  StampedOrderLink,
  StampedOrderNumber,
  StampItem,
  StampNote,
  StampRow,
  StyledLink,
  TextList,
} from './LoyaltyT&Cs.styles'
import { ScallopedMaskSVGLarge } from '@src/components/SVGS/ScallopedMaskSVGLarge'
import { LoyaltyCardStampProgressMeter } from '@src/components/CustomerLoyaltyCard/StampProgressMeter'

const daysOfWeekMap = ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun']

export const LoyaltyTermsAndConditions: React.FC<{ discountId: string }> = ({
  discountId,
}) => {
  const { t } = useTranslation('customerLoyaltyCard')
  const marketplace = useMarketplace()
  const formatCurrency = useFormatCurrency()
  const { setRoute } = useAccountRouter()

  const { data, loading } = useQuery(getSingleLoyaltyCardDocument, {
    variables: {
      id: discountId,
    },
  })

  if (loading) {
    return <LoadingSpinner text="Getting Loyalty Card Details" />
  }

  if (!data) {
    return (
      <BurgerSection header={{ title: t('my_loyalty_cards') }}>
        <div>{t('error_fetching')}</div>
      </BurgerSection>
    )
  }

  const { discount } = data.singleLoyaltyCard
  const loyaltyCard = data.singleLoyaltyCard
  const loyaltyCardStamps = loyaltyCard.loyaltyCardStamps || []
  const sortedDays =
    discount?.daysOfWeek && [...discount.daysOfWeek].sort((a, b) => a - b)

  const getReadableDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return ''

    const dateFormat = 'HH:mm MMM d, yyyy'
    const formattedStartDate = format(parseISO(startDate), dateFormat)
    const formattedEndDate = format(parseISO(endDate), dateFormat)

    return `${formattedStartDate} - ${formattedEndDate}`
  }

  return (
    <BurgerSection
      whiteBackgroundColor={true}
      header={{
        title: t('loyalty_card_details', {
          chosenHeaderName:
            marketplace?.loyaltyCardSectionHeader || t('loyalty_card'),
        }),
      }}
      noPadding={true}
      twoLineHeader={false}
    >
      <Content>
        <LoyaltyCardContainer>
          <ScallopedMaskSVGLarge scale={0.87} />
          <LoyaltyCardImage
            isTermsPage={true}
            canStampBeEarned={true}
            role="img"
            aria-label={discount.id}
            imageUrl={imageJitURL(loyaltyCard.loyaltyCardIcon, {
              resize: {
                width: 180,
                height: 180,
                fit: 'cover',
              },
            })}
            scale={0.87}
          />
        </LoyaltyCardContainer>
        <LoyaltyCardName loyaltyCardColor={loyaltyCard.loyaltyCardColor}>
          {discount.name}
        </LoyaltyCardName>
        <ProgressMeterContainer>
          <LoyaltyCardStampProgressMeter
            loyaltyCard={loyaltyCard}
            isTermsPage={true}
          />
        </ProgressMeterContainer>
        <LoyaltyInfoContainer>
          <LoyaltyCardInfoText
            loyaltyCard={loyaltyCard}
            isTermsPage={true}
            amountToSpendToEarnStamp={'0.00'}
          />
          {loyaltyCardStamps.length &&
          loyaltyCardStamps.length !== loyaltyCard.requiredStamps ? (
            <LoyaltyCardHowManyStampsAwayText>
              {t('how_many_stamps_away', {
                numberOfStamps: `${
                  loyaltyCard.requiredStamps - loyaltyCardStamps.length
                } stamp${
                  loyaltyCard.requiredStamps - loyaltyCardStamps.length !== 1
                    ? 's'
                    : ''
                } `,
              })}
            </LoyaltyCardHowManyStampsAwayText>
          ) : null}
        </LoyaltyInfoContainer>
        <SectionContainer>
          <SectionTitle>{t('participating_businesses')}</SectionTitle>
          {discount?.allowedOutlets && discount.allowedOutlets.length ? (
            discount.allowedOutlets
              .filter(outlet => outlet.active)
              .map((outlet, index) => (
                <TextList key={outlet.id}>
                  <StyledLink
                    to={`/${marketplace.urlPath}/${outlet.id}/${convertToSlug(
                      outlet.displayName
                    )}/menu`}
                  >
                    {outlet.displayName}
                  </StyledLink>
                  {discount.allowedOutlets &&
                    index < discount.allowedOutlets.length - 1 &&
                    ','}
                </TextList>
              ))
          ) : (
            <SectionContent>
              {marketplace.allOutletsText || t('participating_businesses_all')}
            </SectionContent>
          )}
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>{t('valid_dates')}</SectionTitle>
          <SectionContent>
            {discount?.startDate &&
              discount.endDate &&
              getReadableDateRange(discount.startDate, discount.endDate)}
          </SectionContent>
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>{t('valid_days')}</SectionTitle>
          <SectionContent>
            {sortedDays &&
              sortedDays.map((day, index) => (
                <TextList key={day}>
                  {daysOfWeekMap[day - 1]}
                  {index < sortedDays.length - 1 && ','}
                </TextList>
              ))}
          </SectionContent>
        </SectionContainer>
        {discount?.minimumSubtotalGross && discount.minimumSubtotalGross > 0 ? (
          <SectionContainer>
            <SectionTitle>
              {discount.loyaltyCard?.discountStrategy ===
              LoyaltyCardDiscountStrategy.BASKET_SUBTOTAL_DISCOUNT
                ? t('minimum_subtotal_spend')
                : t('minimum_total_spend')}
            </SectionTitle>
            <SectionContent>
              {t('spend_to_qualify', {
                amount: discount?.minimumSubtotalGross
                  ? formatCurrency(discount.minimumSubtotalGross)
                  : formatCurrency(0),
              })}
            </SectionContent>
          </SectionContainer>
        ) : null}
        <SectionContainer>
          <SectionTitle>{t('stamp_value')}</SectionTitle>
          <SectionContent>
            {discount?.discountPercentage
              ? discount.loyaltyCard?.discountStrategy ===
                LoyaltyCardDiscountStrategy.BASKET_SUBTOTAL_DISCOUNT
                ? t('get_percentage_order_subtotal', {
                    value: discount.discountPercentage,
                  })
                : discount.loyaltyCard?.discountStrategy ===
                  LoyaltyCardDiscountStrategy.WHOLE_ORDER_DISCOUNT
                ? t('get_percentage_order_total', {
                    value: discount.discountPercentage,
                  })
                : null
              : discount?.discountAmount
              ? t('get_fixed_value_off', {
                  value: formatCurrency(discount.discountAmount),
                })
              : null}
          </SectionContent>
        </SectionContainer>
        {loyaltyCard.loyaltyCardStamps.length > 0 && (
          <SectionContainer>
            <SectionTitle>{t('orders')}</SectionTitle>
            <SectionContent>
              {loyaltyCard.loyaltyCardStamps.map(stamp => (
                <StampItem key={stamp.id}>
                  {stamp.order ? (
                    <StampedOrderLink
                      type="button"
                      onClick={() => {
                        setRoute({
                          mainRouteName: MainRouteName.ORDERS,
                          pageDataId: stamp?.order?.id,
                        })
                      }}
                    >
                      <StampRow>
                        <StampedOrderNumber>
                          {stamp.order.orderNumber
                            ? `#${stamp.order.orderNumber}`
                            : '-'}
                        </StampedOrderNumber>
                        {stamp.amountEarnedOnOrder > 0 && (
                          <StampedAmount>
                            {formatCurrency(stamp.amountEarnedOnOrder)}
                          </StampedAmount>
                        )}
                      </StampRow>
                      {stamp.loyaltyCardStampNote && (
                        <StampNote>{stamp.loyaltyCardStampNote}</StampNote>
                      )}
                    </StampedOrderLink>
                  ) : (
                    <>
                      <StampRow>
                        -
                        {stamp.amountEarnedOnOrder > 0 && (
                          <StampedAmount>
                            {formatCurrency(stamp.amountEarnedOnOrder)}
                          </StampedAmount>
                        )}
                      </StampRow>
                      {stamp.loyaltyCardStampNote && (
                        <StampNote>{stamp.loyaltyCardStampNote}</StampNote>
                      )}
                    </>
                  )}
                </StampItem>
              ))}
            </SectionContent>
          </SectionContainer>
        )}
        <SectionContainer>
          <SectionTitle>{t('terms_and_conditions')}</SectionTitle>
          <SectionContent>{loyaltyCard.termsAndConditions}</SectionContent>
        </SectionContainer>
      </Content>
    </BurgerSection>
  )
}
